<script setup>
import { ref, onMounted } from "vue";
import NumberAnimation from "vue-number-animation";

const isClient = ref(false);
const number1 = ref(null);
const number2 = ref(null);
const number3 = ref(null);
const number4 = ref(null);
const containerRef = ref(null);

onMounted(() => {
  isClient.value = true;
  console.log("StatsSection mounted, initializing observer");

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          console.log("StatsSection intersecting, starting animation");
          [number1, number2, number3, number4].forEach((refItem) => {
            const instance = Array.isArray(refItem.value)
              ? refItem.value[0]
              : refItem.value;
            if (instance) {
              if (instance.play) {
                instance.play();
              } else if (instance.restart) {
                instance.restart();
              }
            }
          });
        }
      });
    },
    { threshold: 0.5 },
  );

  if (containerRef.value) {
    observer.observe(containerRef.value);
  }
});

const theFormat = (value) => {
  return value.toFixed(1);
};

const randomInRange = (range) => {
  return Math.random() * range;
};

const stats = [
  {
    ref: number1,
    from: 3000000,
    to: () => 5000000 - randomInRange(10000),
    format: (v) => v.toFixed(0),
    label: "累積出貨訂單",
    hasPercent: false,
  },
  {
    ref: number2,
    from: 85.5,
    to: () => 99.9 - randomInRange(10),
    format: theFormat,
    label: "客戶回覆率",
    hasPercent: true,
  },
  {
    ref: number3,
    from: 85.5,
    to: () => 99.9 - randomInRange(10),
    format: theFormat,
    label: "出貨率",
    hasPercent: true,
  },
  {
    ref: number4,
    from: 85.5,
    to: () => 99.9 - randomInRange(10),
    format: theFormat,
    label: "訂單評估效率",
    hasPercent: true,
  },
];
</script>

<template>
  <section class="stats-section">
    <div class="blue-background">
      <div class="stats-header">
        <picture>
          <source
            media="(min-width: 1060px)"
            srcset="@/assets/images/desktop/products/right.svg"
          />
          <img src="@/assets/images/desktop/products/right.svg" alt="" />
        </picture>
        <p class="service-results">服務成果</p>
        <picture>
          <source
            media="(min-width: 1060px)"
            srcset="@/assets/images/desktop/products/left.svg"
          />
          <img src="@/assets/images/desktop/products/left.svg" alt="" />
        </picture>
      </div>

      <div class="count-content" ref="containerRef">
        <div class="count-window">
          <div class="count-inner">
            <template v-for="(stat, index) in stats" :key="stat.label">
              <div class="count-item">
                <div
                  :style="
                    stat.hasPercent ? 'display: flex; flex-direction: row' : ''
                  "
                >
                  <NumberAnimation
                    v-if="isClient"
                    class="count-number"
                    :ref="stat.ref"
                    :from="stat.from"
                    :to="typeof stat.to === 'function' ? stat.to() : stat.to"
                    :format="stat.format"
                    :duration="0.5"
                    :delay="1"
                    :autoplay="false"
                    easing="linear"
                  />
                  <span v-else class="count-number">{{ stat.from }}</span>
                  <p v-if="stat.hasPercent" class="count-percent">%</p>
                </div>
                <div class="count-text">{{ stat.label }}</div>
              </div>

              <!-- Separator Line (except last item) -->
              <div v-if="index < stats.length - 1" class="line"></div>
            </template>
          </div>
        </div>
        <p class="statistics">資料統計截至 2024 年 5 月</p>
      </div>

      <picture style="width: 100%">
        <source
          media="(min-width: 1060px)"
          srcset="@/assets/images/desktop/products/Vector.png"
        />
        <img
          class="stats-bottom-curve"
          src="@/assets/images/desktop/products/Vector1.svg"
          alt="灰底弧度"
        />
      </picture>
    </div>
  </section>
</template>

<style scoped>
/* Stats Section */
.stats-section {
  position: relative;
}

.blue-background {
  background: #5974ff;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.stats-header {
  display: flex;
  padding-top: 120px;
  justify-content: center;
  align-items: center;
}

.service-results {
  font-weight: 600;
  font-size: 26px;
  color: #ffffff;
  white-space: nowrap;
  margin: 0 23px;
  margin-top: 8px;
}

.count-content {
  margin-top: 40px;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.count-window {
  background-color: #fff;
  width: 343px;
  border-radius: 16px;
}

.count-inner {
  height: 100%;
  margin-top: 32px;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.count-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.line {
  border: 0.8px solid #ced4da;
  width: 280px;
  margin-top: 36px;
  margin-bottom: 36px;
  height: 0;
}

.count-number {
  font-weight: 600;
  font-size: 40px;
  line-height: 60px;
  color: #3c5bff;
}

.count-text {
  font-size: 24px;
  color: #868e96;
}

.count-percent {
  font-weight: 600;
  font-size: 40px;
  line-height: 60px;
  color: #3c5bff;
}

.statistics {
  font-size: 18px;
  color: #f8f9fa;
  margin-top: 40px;
  margin-bottom: 100px;
}

.stats-bottom-curve {
  width: 100%;
  margin-bottom: -2px;
}

@media (min-width: 1060px) {
  .stats-header {
    margin-top: 151px;
  }

  .service-results {
    font-size: clamp(32px, 2.63vw, 38px);
  }

  .count-window {
    width: 78.72vw;
    max-width: 1120px;
    height: 230px;
  }

  .count-inner {
    flex-direction: row;
    justify-content: space-around;
    width: 100%;
    margin: 0;
  }

  .line {
    border-right: 1px solid #aabac6;
    height: 130px;
    width: 0;
    border-bottom: none;
    margin: 0;
  }

  .count-number,
  .count-percent {
    font-size: clamp(48px, 3.33vw, 48px);
  }

  .count-text {
    margin-top: 8px;
    font-size: 28px;
  }

  .statistics {
    margin-bottom: 140px;
  }
}
</style>
